import React, { useEffect, useState } from 'react';

import { faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  IconButton,
  Typography,
} from '@mui/material';

import { getDisclosureFactsEndPoint } from '../../common/api.endPoints';
import { Resource } from '../../common/constants/resource';
import { HttpService } from '../../common/http.service';
import { Fact } from '../tasks/types/disclosure';
import './FamilyCoverageSection.scss';

const SIBLING_COVERAGE_FACT = 'sibling_coverage';

const formatCurrency = (
  value: string | number | boolean | undefined,
): string => {
  if (value === undefined || value === null || value === '')
    return Resource.NORA_M_DASH;
  const num = Number(value);
  if (isNaN(num)) return String(value);
  return `$${num.toLocaleString()}`;
};

const getAttr = (fact: Fact, attrName: string) =>
  fact.attributes?.find(a => a.attributeName === attrName)?.value;

export const FamilyCoverageSection = ({
  applicationId,
  onHasContent,
}: {
  applicationId: number;
  onHasContent?: (hasContent: boolean) => void;
}) => {
  const [facts, setFacts] = useState<Fact[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      const data = await HttpService.getInstance().get(
        getDisclosureFactsEndPoint(applicationId),
      );
      if (data && !data.error) {
        setFacts(data.parsedBody?.facts ?? []);
      }
      setLoaded(true);
    })();
  }, [applicationId]);

  const siblingFacts = facts.filter(f => f.factName === SIBLING_COVERAGE_FACT);
  const getFactValue = (factName: string) =>
    facts.find(f => f.factName === factName)?.value;

  const insuredInforce = getFactValue('total_coverage_in_force');
  const insuredPending = getFactValue('coverage_to_be_placed');
  const owner1Inforce = getFactValue('owner_1_total_coverage_in_force');
  const owner1Pending = getFactValue('owner_1_coverage_to_be_placed');
  const owner2Inforce = getFactValue('owner_2_total_coverage_in_force');
  const owner2Pending = getFactValue('owner_2_coverage_to_be_placed');

  const hasInsuredData = [insuredInforce, insuredPending].some(
    v => v !== undefined,
  );
  const hasParentData = [
    owner1Inforce,
    owner1Pending,
    owner2Inforce,
    owner2Pending,
  ].some(v => v !== undefined);

  const hasContent =
    siblingFacts.length > 0 || hasParentData || hasInsuredData;

  useEffect(() => {
    if (loaded) {
      onHasContent?.(hasContent);
    }
  }, [loaded, hasContent]);

  if (!hasContent) {
    return null;
  }

  const insuredTotal =
    (Number(insuredInforce) || 0) + (Number(insuredPending) || 0);
  const owner1Total =
    (Number(owner1Inforce) || 0) + (Number(owner1Pending) || 0);
  const owner2Total =
    (Number(owner2Inforce) || 0) + (Number(owner2Pending) || 0);

  const hasOwner2 = [owner2Inforce, owner2Pending].some(v => v !== undefined);

  return (
    <div id="FamilyCoverage">
      <Accordion expanded={expanded}>
        <AccordionSummary onClick={() => setExpanded(e => !e)}>
          <div className="family-coverage-icon-container">
            <IconButton size="small">
              <FontAwesomeIcon
                icon={faAngleRight}
                rotation={expanded ? 90 : undefined}
              />
            </IconButton>
          </div>
          <Typography variant="h2">Family Coverage Summary</Typography>
        </AccordionSummary>
        <div className="family-coverage-table-wrapper">
          <table className="family-coverage-table">
            <thead>
              <tr>
                <th>Relation</th>
                <th>Inforce</th>
                <th>Applied / pending</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {hasInsuredData && (
                <tr>
                  <td>
                    <Chip
                      label="Insured"
                      size="small"
                      className="family-coverage-chip family-coverage-chip--insured"
                    />
                  </td>
                  <td>{formatCurrency(insuredInforce)}</td>
                  <td>
                    {insuredPending && Number(insuredPending) > 0 ? (
                      <span>
                        <span className="family-coverage-pending">
                          {formatCurrency(insuredPending)}
                        </span>
                        <span className="family-coverage-pending-label">
                          {' '}
                          · pending
                        </span>
                      </span>
                    ) : (
                      Resource.NORA_M_DASH
                    )}
                  </td>
                  <td>
                    {insuredTotal > 0
                      ? formatCurrency(insuredTotal)
                      : Resource.NORA_M_DASH}
                  </td>
                </tr>
              )}
              {hasParentData && (
                <tr>
                  <td>
                    <Chip
                      label="Parent 1"
                      size="small"
                      className="family-coverage-chip family-coverage-chip--parent"
                    />
                    <span className="family-coverage-owner-label">(owner)</span>
                  </td>
                  <td>{formatCurrency(owner1Inforce)}</td>
                  <td>
                    {owner1Pending && Number(owner1Pending) > 0 ? (
                      <span>
                        <span className="family-coverage-pending">
                          {formatCurrency(owner1Pending)}
                        </span>
                        <span className="family-coverage-pending-label">
                          {' '}
                          · pending
                        </span>
                      </span>
                    ) : (
                      Resource.NORA_M_DASH
                    )}
                  </td>
                  <td>
                    {owner1Total > 0
                      ? formatCurrency(owner1Total)
                      : Resource.NORA_M_DASH}
                  </td>
                </tr>
              )}
              {hasOwner2 && (
                <tr>
                  <td>
                    <Chip
                      label="Parent 2"
                      size="small"
                      className="family-coverage-chip family-coverage-chip--parent"
                    />
                  </td>
                  <td>{formatCurrency(owner2Inforce)}</td>
                  <td>
                    {owner2Pending && Number(owner2Pending) > 0 ? (
                      <span>
                        <span className="family-coverage-pending">
                          {formatCurrency(owner2Pending)}
                        </span>
                        <span className="family-coverage-pending-label">
                          {' '}
                          · pending
                        </span>
                      </span>
                    ) : (
                      Resource.NORA_M_DASH
                    )}
                  </td>
                  <td>
                    {owner2Total > 0
                      ? formatCurrency(owner2Total)
                      : Resource.NORA_M_DASH}
                  </td>
                </tr>
              )}
              {siblingFacts.map((fact, idx) => {
                const inforce = getAttr(fact, 'inforce');
                const pending = getAttr(fact, 'pending');
                const total = getAttr(fact, 'total_coverage');
                return (
                  <tr key={idx}>
                    <td>
                      <Chip
                        label={`Sibling ${idx + 1}`}
                        size="small"
                        className="family-coverage-chip family-coverage-chip--sibling"
                      />
                    </td>
                    <td>{formatCurrency(inforce)}</td>
                    <td>
                      {pending && Number(pending) > 0 ? (
                        <span>
                          <span className="family-coverage-pending">
                            {formatCurrency(pending)}
                          </span>
                          <span className="family-coverage-pending-label">
                            {' '}
                            · pending
                          </span>
                        </span>
                      ) : (
                        Resource.NORA_M_DASH
                      )}
                    </td>
                    <td>{formatCurrency(total)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <AccordionDetails></AccordionDetails>
      </Accordion>
    </div>
  );
};
